/**
 * The 41 content locales (SCHULUNG-SPEC §3.4; 16 until 29.09.2026, then +25 by
 * owner decision). Twin of
 * functions/intranet/src/lib/staffTrainingBlocks.js's STAFF_CONTENT_LOCALES —
 * duplicated because the frontend cannot import from functions/. Keep both
 * lists in step; the DB CHECK constraint on
 * ops.StaffTrainingLanguageRelease.locale is the third copy and the authority
 * (migration 339 widened it from 16 to 41 codes).
 *
 * This is the CONTENT language (what the training text is written in), which is
 * separate from the UI language (de/en/fr/tr, `mc-staff-lang`).
 *
 * `countries` are the flags shown next to the language (owner decision
 * 29.09.2026): ISO 3166-1 alpha-2, upper case, ordered by relevance, at most 4,
 * always drawn through shared/ui `CountryFlags`. Deliberately NOT shown because
 * they are politically sensitive in a German workplace: TW for Chinese, KP for
 * Korean, UA for Russian. Kurdish (kmr) is a stateless language: it shows the
 * flag of Kurdistan (red, white, green, golden sun with 21 rays) as our own file
 * under the one custom key 'KURDISTAN' (shared/ui/flags/kurdistan.svg) — no
 * national flag and no neutral symbol.
 */

export type ContentDir = 'ltr' | 'rtl'

export interface ContentLocale {
  locale: string
  name: string
  /** The language's own name for itself, e.g. 'Türkçe' for tr, 'العربية' for
   * ar — SCHULUNG-SPEC §6.13.2's per-training language picker shows this,
   * not the German `name`, so someone who can't read German can still find
   * their language. */
  endonym: string
  dir: ContentDir
  /** Flag countries (ISO 3166-1 alpha-2, upper case, max 4), or the one custom
   * key 'KURDISTAN' (Kurdish). Empty = no flag. */
  countries: readonly string[]
  /** One of the 25 languages added on 29.09.2026. Pickers that list ALL
   * languages offer these only once the database accepts them (migration 339,
   * GET /api/staff-portal/admin/content-locales → `erweitert`) — see
   * `aktiveContentLocales`. Remove the marker after 339 is live in prod. */
  neu?: boolean
  /** Machine translation is weaker for this language: the admin picker says so
   * and a native speaker should read the result before release. */
  lowResource?: boolean
}

export const STAFF_CONTENT_LOCALES: ContentLocale[] = [
  { locale: 'de', name: 'Deutsch', endonym: 'Deutsch', dir: 'ltr', countries: ['DE', 'AT', 'CH'] },
  { locale: 'en', name: 'Englisch', endonym: 'English', dir: 'ltr', countries: ['US', 'GB', 'CA', 'AU'] },
  { locale: 'fr', name: 'Französisch', endonym: 'Français', dir: 'ltr', countries: ['FR', 'CA', 'BE', 'CH'] },
  { locale: 'tr', name: 'Türkisch', endonym: 'Türkçe', dir: 'ltr', countries: ['TR', 'CY'] },
  { locale: 'sq', name: 'Albanisch', endonym: 'Shqip', dir: 'ltr', countries: ['AL', 'XK', 'MK'] },
  { locale: 'ar', name: 'Arabisch', endonym: 'العربية', dir: 'rtl', countries: ['SA', 'EG', 'SY', 'MA'] },
  { locale: 'ar-IQ', name: 'Arabisch (Irak)', endonym: 'العربية (العراق)', dir: 'rtl', countries: ['IQ'] },
  { locale: 'zh', name: 'Chinesisch', endonym: '中文', dir: 'ltr', countries: ['CN', 'SG'] },
  { locale: 'prs', name: 'Dari', endonym: 'دری', dir: 'rtl', countries: ['AF'] },
  { locale: 'hi', name: 'Hindi', endonym: 'हिन्दी', dir: 'ltr', countries: ['IN'] },
  { locale: 'mk', name: 'Mazedonisch', endonym: 'Македонски', dir: 'ltr', countries: ['MK'] },
  { locale: 'pl', name: 'Polnisch', endonym: 'Polski', dir: 'ltr', countries: ['PL'] },
  { locale: 'pt', name: 'Portugiesisch', endonym: 'Português', dir: 'ltr', countries: ['PT', 'BR', 'AO', 'MZ'] },
  { locale: 'ro', name: 'Rumänisch', endonym: 'Română', dir: 'ltr', countries: ['RO', 'MD'] },
  // Serbisch: der Inhalt ist lateinisch geschrieben, also auch der Endonym.
  { locale: 'sr', name: 'Serbisch', endonym: 'Srpski', dir: 'ltr', countries: ['RS', 'BA', 'ME'] },
  { locale: 'es', name: 'Spanisch', endonym: 'Español', dir: 'ltr', countries: ['ES', 'MX', 'CO', 'AR'] },
  // ── Neu seit 29.09.2026 (neu: true) ──
  { locale: 'ru', name: 'Russisch', endonym: 'Русский', dir: 'ltr', countries: ['RU', 'BY', 'KZ'], neu: true },
  { locale: 'uk', name: 'Ukrainisch', endonym: 'Українська', dir: 'ltr', countries: ['UA'], neu: true },
  { locale: 'it', name: 'Italienisch', endonym: 'Italiano', dir: 'ltr', countries: ['IT', 'CH', 'SM'], neu: true },
  { locale: 'el', name: 'Griechisch', endonym: 'Ελληνικά', dir: 'ltr', countries: ['GR', 'CY'], neu: true },
  { locale: 'bg', name: 'Bulgarisch', endonym: 'Български', dir: 'ltr', countries: ['BG'], neu: true },
  { locale: 'hr', name: 'Kroatisch', endonym: 'Hrvatski', dir: 'ltr', countries: ['HR', 'BA'], neu: true },
  { locale: 'bs', name: 'Bosnisch', endonym: 'Bosanski', dir: 'ltr', countries: ['BA'], neu: true },
  { locale: 'hu', name: 'Ungarisch', endonym: 'Magyar', dir: 'ltr', countries: ['HU'], neu: true },
  { locale: 'cs', name: 'Tschechisch', endonym: 'Čeština', dir: 'ltr', countries: ['CZ'], neu: true },
  { locale: 'nl', name: 'Niederländisch', endonym: 'Nederlands', dir: 'ltr', countries: ['NL', 'BE', 'SR'], neu: true },
  { locale: 'fa', name: 'Persisch', endonym: 'فارسی', dir: 'rtl', countries: ['IR'], neu: true },
  // Kurdisch (Kurmandschi): lateinische Schrift, kein Staat — statt einer Landesflagge die Flagge Kurdistans (eigene Datei).
  { locale: 'kmr', name: 'Kurdisch (Kurmandschi)', endonym: 'Kurdî (Kurmancî)', dir: 'ltr', countries: ['KURDISTAN'], neu: true, lowResource: true },
  { locale: 'ps', name: 'Paschtu', endonym: 'پښتو', dir: 'rtl', countries: ['AF', 'PK'], neu: true, lowResource: true },
  { locale: 'ur', name: 'Urdu', endonym: 'اردو', dir: 'rtl', countries: ['PK', 'IN'], neu: true },
  { locale: 'bn', name: 'Bengalisch', endonym: 'বাংলা', dir: 'ltr', countries: ['BD', 'IN'], neu: true },
  { locale: 'vi', name: 'Vietnamesisch', endonym: 'Tiếng Việt', dir: 'ltr', countries: ['VN'], neu: true },
  { locale: 'th', name: 'Thai', endonym: 'ไทย', dir: 'ltr', countries: ['TH'], neu: true },
  { locale: 'fil', name: 'Filipino', endonym: 'Filipino', dir: 'ltr', countries: ['PH'], neu: true },
  { locale: 'id', name: 'Indonesisch', endonym: 'Bahasa Indonesia', dir: 'ltr', countries: ['ID'], neu: true },
  { locale: 'ja', name: 'Japanisch', endonym: '日本語', dir: 'ltr', countries: ['JP'], neu: true },
  { locale: 'ko', name: 'Koreanisch', endonym: '한국어', dir: 'ltr', countries: ['KR'], neu: true },
  { locale: 'ti', name: 'Tigrinya', endonym: 'ትግርኛ', dir: 'ltr', countries: ['ER', 'ET'], neu: true, lowResource: true },
  { locale: 'am', name: 'Amharisch', endonym: 'አማርኛ', dir: 'ltr', countries: ['ET'], neu: true, lowResource: true },
  { locale: 'so', name: 'Somali', endonym: 'Soomaali', dir: 'ltr', countries: ['SO', 'ET', 'DJ', 'KE'], neu: true, lowResource: true },
  { locale: 'sw', name: 'Swahili', endonym: 'Kiswahili', dir: 'ltr', countries: ['TZ', 'KE'], neu: true, lowResource: true },
]

/** The languages a picker that lists ALL of them may offer. The 25 `neu` ones
 * only once the database accepts them (`erweitert` from GET
 * /api/staff-portal/admin/content-locales, i.e. migration 339 is live) —
 * before that the server would answer 400/409 instead of saving them. The
 * staff app itself never needs this: it only shows the languages a training
 * actually has. Order is list order. */
export function aktiveContentLocales(erweitert?: boolean | null): ContentLocale[] {
  return STAFF_CONTENT_LOCALES.filter((l) => !l.neu || erweitert === true)
}

/** Flag countries (ISO 3166-1 alpha-2, or 'KURDISTAN' for Kurdish) of a language,
 * for `CountryFlags`. Empty for a code not in the list — never throws, so a
 * future/unknown code just renders without flags. */
export function localeCountries(locale: string): readonly string[] {
  return STAFF_CONTENT_LOCALES.find((l) => l.locale === locale)?.countries ?? []
}

/** Up to this many languages a training's language choice stays a pill row;
 * beyond it the pills no longer fit next to each other on a phone (owner
 * decision 29.09.2026) and the choice becomes a Dropdown. */
export const MAX_SPRACH_PILLEN = 3

export function sprachwahlAlsDropdown(anzahlSprachen: number): boolean {
  return anzahlSprachen > MAX_SPRACH_PILLEN
}

export function localeName(locale: string): string | null {
  return STAFF_CONTENT_LOCALES.find((l) => l.locale === locale)?.name ?? null
}

/** The language's own name for itself (§6.13.2 picker). Falls back to the
 * raw locale code for one not in the canonical 41 — never blank. */
export function localeEndonym(locale: string): string {
  return STAFF_CONTENT_LOCALES.find((l) => l.locale === locale)?.endonym ?? locale
}

export function localeDir(locale: string): ContentDir {
  return STAFF_CONTENT_LOCALES.find((l) => l.locale === locale)?.dir ?? 'ltr'
}

// ── Per-training language choice (§6.13.2 Aufgabe 68 / §6.14.1 Aufgabe 69) ──
// Kept in localStorage, keyed by mainId + training_id — read/written by
// BOTH the Schulungen list (language chips on the card) and the
// Schulung/Quiz detail pages, so a chip tap on the list and the switch on
// the detail page agree on the same key.
//
// §6.18 "Bug-Fund" (2026-09-09): location PCs are shared between people, so
// the key MUST be scoped by mainId — otherwise a colleague's language pick
// (or, for the walk-gate below, their completed Ablauf-Training) leaks into
// the next person's login at the same terminal. StaffAuthProvider's logout
// clears every `mc-staff-locale:`/`mc-staff-walk:` key on sign-out as a
// second line of defense.
const LOCALE_STORAGE_PREFIX = 'mc-staff-locale:'

export function readStaffLocale(mainId: string, trainingId: string): string | null {
  try { return window.localStorage.getItem(`${LOCALE_STORAGE_PREFIX}${mainId}:${trainingId}`) } catch { return null }
}

export function writeStaffLocale(mainId: string, trainingId: string, locale: string): void {
  try { window.localStorage.setItem(`${LOCALE_STORAGE_PREFIX}${mainId}:${trainingId}`, locale) } catch { /* ignore */ }
}

// ── Ablauf-Training walk gate (§6.14.3 Aufgabe 71, fixed for shared PCs by
// §6.18's Bug-Fund) — one key per (person, training, release, decision
// block). The version_label is part of the key so a new release of the
// training requires the walk again, same as the original per-training
// design; mainId scopes it per person, same as the locale choice above.
const WALK_STORAGE_PREFIX = 'mc-staff-walk:'

export function readWalkDone(mainId: string, trainingId: string, versionLabel: string, blockIndex: number): boolean {
  try { return window.localStorage.getItem(`${WALK_STORAGE_PREFIX}${mainId}:${trainingId}:${versionLabel}:${blockIndex}`) === '1' } catch { return false }
}

export function writeWalkDone(mainId: string, trainingId: string, versionLabel: string, blockIndex: number): void {
  try { window.localStorage.setItem(`${WALK_STORAGE_PREFIX}${mainId}:${trainingId}:${versionLabel}:${blockIndex}`, '1') } catch { /* ignore */ }
}

// ── Reading position ("Weiterlesen", §6.32 Aufgabe 107) — one key per
// (person, training, release): the CardDeck card index last shown, so a
// revisit resumes exactly there and the Schulungen list can show "Weiterlesen
// · Abschnitt n von m". Same shared-PC scoping as the locale/walk keys above
// (§6.18 Bug-Fund) — StaffAuthProvider's logout clears every `mc-staff-read:`
// key alongside `mc-staff-locale:`/`mc-staff-walk:`. The pointer is also
// cleared explicitly once the reading step is confirmed (StaffSchulungDetailPage)
// — nothing is left to resume once progress moves past 'offen'.
const READ_STORAGE_PREFIX = 'mc-staff-read:'

export function readReadPosition(mainId: string, trainingId: string, versionLabel: string): number | null {
  try {
    const raw = window.localStorage.getItem(`${READ_STORAGE_PREFIX}${mainId}:${trainingId}:${versionLabel}`)
    if (raw == null) return null
    const n = Number(raw)
    return Number.isInteger(n) && n >= 0 ? n : null
  } catch { return null }
}

export function writeReadPosition(mainId: string, trainingId: string, versionLabel: string, cardIndex: number): void {
  try { window.localStorage.setItem(`${READ_STORAGE_PREFIX}${mainId}:${trainingId}:${versionLabel}`, String(cardIndex)) } catch { /* ignore */ }
}

export function clearReadPosition(mainId: string, trainingId: string, versionLabel: string): void {
  try { window.localStorage.removeItem(`${READ_STORAGE_PREFIX}${mainId}:${trainingId}:${versionLabel}`) } catch { /* ignore */ }
}

// ── Last-seen Fassung (§6.41.5 Nr. 2 / §6.41.8, Aufgabe 121) ────────────────
// One key per (person, training) — NOT per version, unlike the read-position/
// walk keys above, because its whole job is remembering the version the
// person saw LAST TIME so a change can be detected on the next load. The
// read-position key above already discards itself for free on a Fassung
// change (its key literally contains the old version_label, so a new label
// just never matches it) — nothing to build there, per the task brief.
// Same shared-PC scoping (§6.18 Bug-Fund): StaffAuthProvider's logout clears
// `mc-staff-version-seen:` alongside the locale/walk/read prefixes.
const VERSION_SEEN_PREFIX = 'mc-staff-version-seen:'

export function readSeenVersionLabel(mainId: string, trainingId: string): string | null {
  try { return window.localStorage.getItem(`${VERSION_SEEN_PREFIX}${mainId}:${trainingId}`) } catch { return null }
}

export function writeSeenVersionLabel(mainId: string, trainingId: string, versionLabel: string): void {
  try { window.localStorage.setItem(`${VERSION_SEEN_PREFIX}${mainId}:${trainingId}`, versionLabel) } catch { /* ignore */ }
}

/** Fortschritt gehört zum tatsächlich gelesenen Sprachstand, nie nur zum Fassungslabel.
 * Alte Schlüssel ohne Sprache/Release sind nicht beweisbar kompatibel und werden nicht übernommen. */
export interface InhaltsIdentitaet {
  publication_version_id?: string | null
  release_owner_version_id?: string | null
  release_id?: string | null
  content_hash?: string | null
  locale?: string | null
  version_label?: string | null
  runde_seit?: string | null
}
export function inhaltsSchluessel(d: InhaltsIdentitaet): string {
  return JSON.stringify(['inhalt', d.publication_version_id ?? '', d.locale ?? '', d.release_owner_version_id ?? '', d.release_id ?? '',
    d.content_hash ?? '', d.release_id ? '' : d.version_label ?? '', d.runde_seit ?? ''])
}

export function normalisiereInhaltsIdentitaet<T extends InhaltsIdentitaet>(d: T): T & { publication_version_id: string | null; release_owner_version_id: string | null; release_id: string | null; content_hash: string | null } {
  return { ...d,
    publication_version_id: d.publication_version_id == null ? null : String(d.publication_version_id),
    release_owner_version_id: d.release_owner_version_id == null ? null : String(d.release_owner_version_id),
    release_id: d.release_id == null ? null : String(d.release_id),
    content_hash: d.content_hash ?? null,
  }
}
